import {
  FREESTAR_STICKY_FOOTER_MOBILE_ENABLED,
  FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT,
  FREESTAR_STICKY_FOOTER_PLACEMENT,
  resolveFreestarStickyFooterPlacement,
} from './placements';
import { isFreestarStickyFooterDismissed, persistFreestarStickyFooterDismiss, FREESTAR_STICKY_FOOTER_DISMISS_STORAGE_KEY } from './stickyFooterDom';

// https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products

export type FreestarDisabledProducts = {
  googleInterstitial?: boolean;
  stickyFooter?: boolean;
  stickyRail?: boolean;
};

type FreestarWindowConfig = {
  channel?: string;
  disabledProducts?: FreestarDisabledProducts;
  enabled_slots?: unknown[];
};

type FreestarIdentityPayload = { email: string } | { hashes: { md5?: string; sha1?: string; sha256?: string } };

type FreestarPlacementConfig = {
  name?: string;
  stickyRailOptions?: {
    active?: boolean;
    isOnCall?: boolean;
  };
};

type FreestarQueue = Array<() => void>;

type FreestarWindow = {
  queue?: FreestarQueue;
  config?: FreestarWindowConfig;
  deleteStickyFooter?: (placementName: string) => void;
  disableRefresh?: () => void;
  enableRefresh?: () => void;
  newStickyFooter?: (placementName: string) => void;
  fsdata?: {
    placements?: FreestarPlacementConfig[];
  };
  identity?: {
    setIdentity?: (payload: FreestarIdentityPayload) => void;
  };
};

declare global {
  interface Window {
    freestar?: FreestarWindow;
  }
}

let stickyFooterDisabledForPath: string | null = null;
let stickyFooterDeletedByPaywall = false;
let lastStickyFooterDisabled: boolean | null = null;
let lastStickyFooterPlacement: string | null = null;
let queuedStickyFooterPlacement: string | null = null;
let queuedStickyFooterFor: FreestarWindow | null = null;

const isStickyFooterLockedForCurrentPath = (): boolean =>
  typeof window !== 'undefined' && stickyFooterDisabledForPath === window.location.pathname;

const currentStickyFooterPlacement = (): string => resolveFreestarStickyFooterPlacement(window.innerWidth);

let pendingFreestarRuns: Array<() => void> = [];
let listeningForPubfigReady = false;
let pubfigReadyRaf = 0;

const isFreestarApiReady = (freestar?: FreestarWindow): freestar is FreestarWindow =>
  typeof freestar?.newStickyFooter === 'function' && !!freestar.fsdata?.placements?.length;

const ensureFreestarWindow = (): { freestar: FreestarWindow; queue: FreestarQueue } => {
  const freestar = (window.freestar ??= {});
  const queue = (freestar.queue ??= []);
  return { freestar, queue };
};

const flushPendingFreestarRuns = (): void => {
  if (!pendingFreestarRuns.length) return;

  const freestar = window.freestar;
  if (!isFreestarApiReady(freestar)) {
    if (pubfigReadyRaf < 300) {
      pubfigReadyRaf += 1;
      requestAnimationFrame(flushPendingFreestarRuns);
    }
    return;
  }

  pubfigReadyRaf = 0;
  const queue = (freestar.queue ??= []);
  const runs = pendingFreestarRuns;
  pendingFreestarRuns = [];
  runs.forEach(run => queue.push(run));
};

const queueFreestar = (run: () => void): void => {
  let didRun = false;
  const runOnce = (): void => {
    if (didRun || !isFreestarApiReady(window.freestar)) return;
    didRun = true;
    run();
  };

  const { freestar, queue } = ensureFreestarWindow();
  queue.push(runOnce);

  if (isFreestarApiReady(freestar)) return;

  pendingFreestarRuns.push(runOnce);
  if (listeningForPubfigReady) return;

  listeningForPubfigReady = true;
  document.addEventListener('load', flushPendingFreestarRuns, true);
  requestAnimationFrame(flushPendingFreestarRuns);
};

const queueDeleteStickyFooter = (placementName: string): void => {
  queueFreestar(() => {
    window.freestar?.deleteStickyFooter?.(placementName);
  });
};

const queueDeleteAllStickyFooters = (): void => {
  queueDeleteStickyFooter(FREESTAR_STICKY_FOOTER_PLACEMENT);
  queueDeleteStickyFooter(FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT);
};

const syncStickyFooterProduct = (shouldDisable: boolean): void => {
  const desiredPlacement = currentStickyFooterPlacement();

  if (shouldDisable) {
    if (lastStickyFooterDisabled !== true) {
      queueDeleteAllStickyFooters();
    }
    lastStickyFooterDisabled = true;
    lastStickyFooterPlacement = null;
    queuedStickyFooterPlacement = null;
    queuedStickyFooterFor = null;
    return;
  }

  const shouldReinject = lastStickyFooterDisabled === true || stickyFooterDeletedByPaywall;
  const placementChanged = lastStickyFooterPlacement != null && lastStickyFooterPlacement !== desiredPlacement;
  const alreadyQueuedForCurrentFreestar =
    queuedStickyFooterFor === window.freestar && queuedStickyFooterPlacement === desiredPlacement;
  const shouldCallNew =
    lastStickyFooterPlacement !== desiredPlacement &&
    !alreadyQueuedForCurrentFreestar &&
    (shouldReinject || desiredPlacement === FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT || placementChanged);

  stickyFooterDeletedByPaywall = false;
  stickyFooterDisabledForPath = null;
  lastStickyFooterDisabled = false;

  if (placementChanged && lastStickyFooterPlacement) {
    queueDeleteStickyFooter(lastStickyFooterPlacement);
  }

  if (shouldCallNew) {
    const placementName = desiredPlacement;
    const freestarForQueue = window.freestar ?? null;
    queuedStickyFooterPlacement = placementName;
    queuedStickyFooterFor = freestarForQueue;
    if (placementName === FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT) {
      queueDeleteStickyFooter(FREESTAR_STICKY_FOOTER_PLACEMENT);
    }
    queueFreestar(() => {
      const freestar = window.freestar;
      if (typeof freestar?.newStickyFooter !== 'function') return;
      freestar.newStickyFooter(placementName);
      lastStickyFooterPlacement = placementName;
    });
    return;
  }

  if (!alreadyQueuedForCurrentFreestar) {
    lastStickyFooterPlacement = desiredPlacement;
  }
};

export const ensureFreestarConfig = ({
  channel,
  disabledProducts,
}: {
  channel: string;
  disabledProducts?: FreestarDisabledProducts;
}): void => {
  if (typeof window === 'undefined') return;

  const { freestar } = ensureFreestarWindow();
  freestar.config = freestar.config || {};
  freestar.config.channel = channel;

  const shouldDisableStickyFooter =
    !!disabledProducts?.stickyFooter ||
    isStickyFooterLockedForCurrentPath() ||
    isFreestarStickyFooterDismissed() ||
    (!FREESTAR_STICKY_FOOTER_MOBILE_ENABLED &&
      currentStickyFooterPlacement() === FREESTAR_STICKY_FOOTER_MOBILE_PLACEMENT);

  // https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products
  freestar.config.disabledProducts = {
    ...(freestar.config.disabledProducts || {}),
    googleInterstitial: !!disabledProducts?.googleInterstitial,
    stickyFooter: shouldDisableStickyFooter,
    stickyRail: !!disabledProducts?.stickyRail,
  };

  syncStickyFooterProduct(shouldDisableStickyFooter);
};

const STATIC_SIDEBAR_PLACEMENT = 'benzinga-com_sidebar_1';

const clearStaticSidebarStickyRail = (freestar: FreestarWindow): void => {
  const placement = freestar.fsdata?.placements?.find(entry => entry.name === STATIC_SIDEBAR_PLACEMENT);
  if (placement?.stickyRailOptions?.active) {
    placement.stickyRailOptions.active = false;
  }
};

// PubOS marks benzinga-com_sidebar_1 as Sticky Rail. While that flag is active,
// freestar.newAdSlots silently drops the placement — so in-layout React slots
// never fill. Clear it once pubfig has loaded so our ArticleSidebar mounts win.
export const neutralizeFreestarStickyRailForReactSlots = (): void => {
  if (typeof window === 'undefined') return;

  const { freestar, queue } = ensureFreestarWindow();

  if (freestar.fsdata?.placements?.length) {
    clearStaticSidebarStickyRail(freestar);
    return;
  }

  queue.push(() => {
    clearStaticSidebarStickyRail(window.freestar || freestar);
  });
};

// https://freestarhelp.zendesk.com/hc/en-us/articles/34516941197460-Disabling-Products
export const disableFreestarStickyFooter = (): void => {
  if (typeof window === 'undefined') return;

  stickyFooterDisabledForPath = window.location.pathname;
  stickyFooterDeletedByPaywall = true;
  lastStickyFooterDisabled = true;
  lastStickyFooterPlacement = null;
  queuedStickyFooterPlacement = null;
  queuedStickyFooterFor = null;

  const { freestar } = ensureFreestarWindow();
  freestar.config = freestar.config || {};
  freestar.config.disabledProducts = {
    ...(freestar.config.disabledProducts || {}),
    stickyFooter: true,
  };

  queueDeleteAllStickyFooters();
};

export const dismissFreestarStickyFooter = (): void => {
  persistFreestarStickyFooterDismiss();
  disableFreestarStickyFooter();
};

export const syncFreestarRefreshWithDocumentVisibility = (): void => {
  if (typeof window === 'undefined') return;

  const { queue } = ensureFreestarWindow();
  queue.push(() => {
    const freestar = window.freestar;
    if (document.hidden) {
      freestar?.disableRefresh?.();
      return;
    }
    freestar?.enableRefresh?.();
  });
};

export const subscribeFreestarRefreshToDocumentVisibility = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  syncFreestarRefreshWithDocumentVisibility();
  document.addEventListener('visibilitychange', syncFreestarRefreshWithDocumentVisibility);
  return () => document.removeEventListener('visibilitychange', syncFreestarRefreshWithDocumentVisibility);
};

export const resetFreestarStickyFooterPaywallLockForTests = (): void => {
  stickyFooterDisabledForPath = null;
  stickyFooterDeletedByPaywall = false;
  lastStickyFooterDisabled = null;
  lastStickyFooterPlacement = null;
  queuedStickyFooterPlacement = null;
  queuedStickyFooterFor = null;
  pendingFreestarRuns = [];
  pubfigReadyRaf = 0;
  try {
    window.localStorage?.removeItem(FREESTAR_STICKY_FOOTER_DISMISS_STORAGE_KEY);
  } catch {
    return;
  }
};
